import React, { useMemo, useState } from "react";
import type { Team } from "../../../types/team";
import styles from "./WeekSelect.module.css";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const startOfWeek = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfWeek = (date: Date) => {
  const d = startOfWeek(date);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
};

const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addMonths = (date: Date, amount: number) => {
  const next = new Date(date);
  next.setMonth(next.getMonth() + amount);
  return next;
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isWithinRange = (date: Date, start: Date, end: Date) =>
  date >= start && date <= end;

const isWeekSelectable = (weekEnd: Date) => {
  const todayStart = startOfDay(new Date());
  return weekEnd < todayStart;
};

const buildCalendarWeeks = (monthDate: Date) => {
  const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const monthEnd = new Date(
    monthDate.getFullYear(),
    monthDate.getMonth() + 1,
    0
  );

  const firstGridDate = startOfWeek(monthStart);
  const lastGridDate = endOfWeek(monthEnd);

  const days: Date[] = [];
  for (
    let d = new Date(firstGridDate);
    d <= lastGridDate;
    d.setDate(d.getDate() + 1)
  ) {
    days.push(new Date(d));
  }

  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return weeks;
};

const formatWeekLabel = (weekStart: Date, weekEnd: Date) => {
  const startLabel = `${MONTH_NAMES[weekStart.getMonth()]} ${weekStart.getDate()}`;
  const endLabel = `${MONTH_NAMES[weekEnd.getMonth()]} ${weekEnd.getDate()}`;
  return `${startLabel} - ${endLabel}, ${weekEnd.getFullYear()}`;
};

type TeamWeekCalendarSelectProps = {
  teams: Team[];
  selectedDate: Date;
  onDateChange: (date: Date) => void;
};

export const WeekSelect = ({
  teams,
  selectedDate,
  onDateChange,
}: TeamWeekCalendarSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(new Date(selectedDate));

  const weeks = useMemo(() => buildCalendarWeeks(visibleMonth), [visibleMonth]);
  const selectedWeekStart = useMemo(
    () => startOfWeek(selectedDate),
    [selectedDate]
  );
  const selectedWeekEnd = useMemo(
    () => endOfWeek(selectedDate),
    [selectedDate]
  );

  const handlePrevMonth = () => setVisibleMonth((prev) => addMonths(prev, -1));
  const handleNextMonth = () => setVisibleMonth((prev) => addMonths(prev, 1));

  const handleSelectWeek = (date: Date, weekEnd: Date) => {
    if (!isWeekSelectable(weekEnd)) {
      return;
    }
    onDateChange(date);
    setIsOpen(false);
  };

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {formatWeekLabel(selectedWeekStart, selectedWeekEnd)}
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.header}>
            <button
              type="button"
              className={styles.navButton}
              onClick={handlePrevMonth}
            >
              ←
            </button>

            <div className={styles.monthLabel}>
              {MONTH_NAMES[visibleMonth.getMonth()]}{" "}
              {visibleMonth.getFullYear()}
            </div>

            <button
              type="button"
              className={styles.navButton}
              onClick={handleNextMonth}
            >
              →
            </button>
          </div>

          <div className={styles.dayNames}>
            {DAY_NAMES.map((day) => (
              <div key={day} className={styles.dayName}>
                {day}
              </div>
            ))}
          </div>

          <div className={styles.weeks}>
            {weeks.map((week, idx) => {
              const weekStart = week[0];
              const weekEnd = week[6];
              const selectable = isWeekSelectable(weekEnd);

              const isSelected =
                isSameDay(weekStart, selectedWeekStart) &&
                isSameDay(weekEnd, selectedWeekEnd);

              return (
                <button
                  key={idx}
                  type="button"
                  disabled={!selectable}
                  onClick={() => handleSelectWeek(weekStart, weekEnd)}
                  className={[
                    styles.weekRow,
                    isSelected && styles.weekRowSelected,
                    !selectable && styles.weekRowDisabled,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {week.map((date) => {
                    const inCurrentMonth =
                      date.getMonth() === visibleMonth.getMonth();

                    const inSelectedWeek = isWithinRange(
                      date,
                      selectedWeekStart,
                      selectedWeekEnd
                    );

                    return (
                      <div
                        key={date.toISOString()}
                        className={[
                          styles.dayCell,
                          !inCurrentMonth && styles.dayCellOutsideMonth,
                          inSelectedWeek && styles.dayCellInSelectedWeek,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {date.getDate()}
                      </div>
                    );
                  })}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
